/**
 * Helpers de fecha del front. Puros y sin dependencias: la lógica de zona
 * horaria vive en el back, acá solo se formatea lo que ya llegó resuelto.
 *
 * El portal muestra las fechas en dd/MM/aaaa, que es como se leen acá. El
 * formato ISO queda para el cable —la API, los `value` de los campos y las
 * claves de React—: es el que ordena bien y el que no se presta a confusión
 * entre día y mes.
 */

/** `YYYY-MM-DD` de hoy, con un desplazamiento opcional en días. */
export function fechaHoy(offsetDias = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return d.toISOString().slice(0, 10);
}

/**
 * `2026-09-11` → `11/09/2026`.
 *
 * Acepta cualquier texto que empiece con una fecha ISO (`2026-09-11T14:05:09Z`
 * o `2026-09-11 14:05`) y devuelve el resto intacto: así sirve igual para una
 * fecha sola que para una fecha con hora.
 */
export function aDdMmAaaa(valor: string | null | undefined): string {
  if (!valor) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})(.*)$/.exec(valor);
  if (!m) return valor;
  const [, a, mes, dia, resto] = m as unknown as [string, string, string, string, string];
  return `${dia}/${mes}/${a}${resto}`;
}

/**
 * Instante ISO → `11/09/2026 14:05:09` en la zona operativa.
 *
 * Convierte de verdad, no recorta el texto. El back manda los instantes en
 * UTC (`...T14:05:09.000Z`) y recortarlo mostraba esa hora tal cual: un
 * ingreso de las 16:23 se veía como 19:23. Los días calendario —una fecha
 * operativa, el inicio de un viaje— NO pasan por acá: esos ya vienen en hora
 * local y convertirlos los correría.
 *
 * `zona` es la del portal (`PORTAL_TZ`, que viaja en `/estado`). Sin ella se
 * usa la del navegador, que para quien opera desde acá es la misma.
 */
export function fechaHoraLegible(iso: string, zona?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const partes = new Intl.DateTimeFormat('es-AR', {
    ...(zona !== undefined ? { timeZone: zona } : {}),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // h23 y no h24: con h24 la medianoche se muestra como 24:00.
    hourCycle: 'h23',
  }).formatToParts(d);

  const parte = (tipo: Intl.DateTimeFormatPartTypes): string =>
    partes.find((p) => p.type === tipo)?.value ?? '';

  return `${parte('day')}/${parte('month')}/${parte('year')} ${parte('hour')}:${parte('minute')}:${parte('second')}`;
}

/** Fecha sola, en dd/MM/aaaa, o guion si no hay valor. */
export function soloFecha(iso: string | null | undefined): string {
  return iso ? aDdMmAaaa(iso.slice(0, 10)) : '—';
}

/**
 * `11/09/2026` → `2026-09-11`, o `null` si no es una fecha real.
 *
 * Valida el día contra el mes —no alcanza con el formato: `31/02/2026` lo
 * cumple y no existe— reconstruyendo la fecha y comparando. Así un dedazo se
 * rechaza en el campo y no llega a la API como un rango vacío inexplicable.
 */
export function aIso(texto: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto.trim());
  if (!m) return null;
  const [, dia, mes, anio] = m as unknown as [string, string, string, string];
  const d = new Date(`${anio}-${mes}-${dia}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10) === `${anio}-${mes}-${dia}` ? `${anio}-${mes}-${dia}` : null;
}

/**
 * Máscara de tipeo: va poniendo las barras a medida que se escriben dígitos.
 *
 * Se ignora todo lo que no sea dígito en vez de rechazar la tecla, para que
 * pegar `11-09-2026` o `11/09/2026` funcione igual que tipear `11092026`.
 */
export function enmascararFecha(texto: string): string {
  const d = texto.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Lo mismo para una hora, en 24 h y con dos puntos.
 *
 * Existe por la misma razón que la máscara de fecha: el `<input type="time">`
 * nativo muestra AM/PM cuando el navegador está en inglés, sin importar el
 * `lang` de la página. Los horarios del proceso se cargan y se leen en 24 h
 * —así se hablan en la operación—, y a qué hora corre el proceso no puede
 * depender del idioma que tenga instalado quien lo configura.
 */
export function enmascararHora(texto: string): string {
  /*
   * Si quien escribe puso los dos puntos, se respetan donde los puso. Sin
   * esto, tipear "8:30" —que es como se dice una hora— daba "83:0": se
   * descartaba el separador y se volvía a insertar a los dos dígitos.
   */
  if (texto.includes(':')) {
    const [h = '', m = ''] = texto.split(':');
    return `${h.replace(/\D/g, '').slice(0, 2)}:${m.replace(/\D/g, '').slice(0, 2)}`;
  }
  const d = texto.replace(/\D/g, '').slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2)}`;
}

/**
 * Completa la hora al salir del campo: `8:30` → `08:30`.
 *
 * Solo agrega el cero de la hora, y solo si los minutos ya están completos.
 * No se adivina nada más: con `8:3` no se sabe si quiso decir 08:03 u 08:30,
 * y rellenarlo por él haría que el proceso corra a una hora que nadie pidió.
 */
export function normalizarHora(texto: string): string {
  const m = /^(\d):([0-5]\d)$/.exec(texto.trim());
  return m ? `0${m[1] ?? ''}:${m[2] ?? ''}` : texto;
}

/** `HH:mm` entre 00:00 y 23:59, o `null` si no es una hora. */
export function aHoraValida(texto: string): string | null {
  const limpio = texto.trim();
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(limpio) ? limpio : null;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

/**
 * `2026-07` → `Julio 2026`.
 *
 * Un período es un **mes y un año**, y así se lee en pantalla. El `AAAA-MM`
 * queda para el cable y para las claves, igual que las fechas: ordena bien y
 * no se presta a confusión. Es además el formato que espera Visma en
 * `periodFrom`.
 */
export function mesYAnio(periodo: string | null | undefined): string {
  if (!periodo) return '—';
  const m = /^(\d{4})-(\d{2})/.exec(periodo);
  if (!m) return periodo;
  const [, anio, mes] = m as unknown as [string, string, string];
  return `${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}
